import { useSettings } from "@/context/SettingsContext";
import { CHARGE_CURRENCY, DEFAULT_CURRENCIES, formatAmount } from "@/lib/currencies";
import { directionOf, localeOf } from "@/lib/languages";

/**
 * One way to print money, dates, times, durations and counts.
 *
 * Money is not formatted here: it goes through lib/currencies (formatAmount,
 * the same function SettingsContext's formatPrice calls), so a price reads
 * the same in a card, a summary and an e-mail. The rest is Intl in the
 * site's language, never the browser's, with Latin digits in every language
 * (formatAmount already prints prices that way; a date beside a price must
 * not switch digit systems).
 *
 * Times come in three kinds, and each keeps its meaning:
 * - "2026-10-23", a calendar date (check-in, pick-up): printed as that day
 *   wherever the reader is, never the day before for a reader behind UTC.
 * - "2026-10-23T14:05", a wall-clock time with no offset (a flight's local
 *   departure, an operator's slot): printed as written, at the place it
 *   happens.
 * - an instant (a Date, a timestamp, or an ISO string with Z or an offset,
 *   such as a cancellation deadline): printed in `timeZone`, the service's
 *   own zone (Europe/Lisbon), or in the reader's zone when none is given.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

const LATN = { numberingSystem: "latn" };

// Intl formatters are slow to build and immutable: one per locale + options.
const cache = new Map();
function formatter(Kind, locale, options) {
  const key = `${Kind.name}|${locale}|${JSON.stringify(options)}`;
  let made = cache.get(key);
  if (!made) {
    made = new Kind(locale, options);
    cache.set(key, made);
  }
  return made;
}

/** The Intl locale of a site language (fr-FR, en-US, ar...). */
export const intlLocale = (lang) => localeOf(lang);

function readMoment(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : { date, kind: "instant" };
  }
  const text = String(value).trim();
  const day = text.match(DATE_ONLY);
  if (day) return { date: new Date(Date.UTC(+day[1], +day[2] - 1, +day[3])), kind: "day" };
  const wall = text.match(WALL_CLOCK);
  if (wall) {
    const [, y, mo, d, h, mi, s] = wall;
    return { date: new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0))), kind: "wall" };
  }
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : { date, kind: "instant" };
}

// A calendar date or a wall-clock time is already local to its place: it is
// printed in UTC, where it was built, so no zone moves it.
function print(value, lang, timeZone, parts) {
  const moment = readMoment(value);
  if (!moment) return "";
  const zone = moment.kind === "instant" ? timeZone : "UTC";
  const locale = localeOf(lang);
  try {
    return formatter(Intl.DateTimeFormat, locale, { ...parts, ...LATN, ...(zone ? { timeZone: zone } : {}) }).format(moment.date);
  } catch {
    // An unknown zone name: the reader's own zone rather than nothing.
    return formatter(Intl.DateTimeFormat, locale, { ...parts, ...LATN }).format(moment.date);
  }
}

const timeParts = (lang) => ({
  hour: lang === "en" ? "numeric" : "2-digit",
  minute: "2-digit",
  // 24-hour everywhere except American English.
  hourCycle: lang === "en" ? "h12" : "h23",
});

/** "ven. 23 oct." · "Fri, Oct 23". `weekday: false` drops the day name, `year: true` adds the year. */
export function formatDate(value, { lang = "fr", timeZone, weekday = true, year = false, month = "short" } = {}) {
  return print(value, lang, timeZone, {
    ...(weekday ? { weekday: "short" } : {}),
    day: "numeric",
    month,
    ...(year ? { year: "numeric" } : {}),
  });
}

/** "14:05" · "2:05 PM". */
export function formatTime(value, { lang = "fr", timeZone } = {}) {
  return print(value, lang, timeZone, timeParts(lang));
}

/** "ven. 23 oct., 14:05": a deadline or a departure, date and time together. */
export function formatDateTime(value, { lang = "fr", timeZone, weekday = true, year = false } = {}) {
  return print(value, lang, timeZone, {
    ...(weekday ? { weekday: "short" } : {}),
    day: "numeric",
    month: "short",
    ...(year ? { year: "numeric" } : {}),
    ...timeParts(lang),
  });
}

/**
 * "12 – 16 nov." · "Nov 12 – 16": two days, short, in the language's own
 * order (Intl formatRange), as the apps' summaries and dates sheet write a
 * stay. Calendar dates ("2026-11-12") print as those days wherever the reader is.
 */
export function formatShortRange(start, end, { lang = "fr" } = {}) {
  const from = readMoment(start);
  const to = readMoment(end);
  if (!from || !to) return formatDate(start || end, { lang, weekday: false });
  const zone = from.kind === "instant" ? undefined : "UTC";
  const shown = formatter(Intl.DateTimeFormat, localeOf(lang), { day: "numeric", month: "short", ...LATN, ...(zone ? { timeZone: zone } : {}) });
  try {
    return shown.formatRange(from.date, to.date).replace(/\s*–\s*/, " – ");
  } catch {
    return `${shown.format(from.date)} – ${shown.format(to.date)}`;
  }
}

/** "ven. 23 oct. → lun. 26 oct.". The arrow follows the reading direction (← in Arabic). */
export function formatDateRange(start, end, options = {}) {
  const from = formatDate(start, options);
  const to = formatDate(end, options);
  if (!from || !to) return from || to;
  const arrow = directionOf(options.lang || "fr") === "rtl" ? "←" : "→";
  return `${from}\u00A0${arrow} ${to}`;
}

/** Whole calendar days from `start` to `end` in `timeZone`: the "+1" of an overnight flight. */
export function dayDifference(start, end, { timeZone } = {}) {
  const a = calendarDay(start, timeZone);
  const b = calendarDay(end, timeZone);
  return a == null || b == null ? null : Math.round((b - a) / 86400000);
}

function calendarDay(value, timeZone) {
  const moment = readMoment(value);
  if (!moment) return null;
  if (moment.kind !== "instant") {
    const d = moment.date;
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  let parts;
  try {
    parts = formatter(Intl.DateTimeFormat, "en-US", { year: "numeric", month: "numeric", day: "numeric", ...(timeZone ? { timeZone } : {}) }).formatToParts(moment.date);
  } catch {
    parts = formatter(Intl.DateTimeFormat, "en-US", { year: "numeric", month: "numeric", day: "numeric" }).formatToParts(moment.date);
  }
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(get("year"), get("month") - 1, get("day"));
}

/** A number in the site's language, Latin digits: "1 240" · "1,240". */
export function formatNumber(value, { lang = "fr", ...options } = {}) {
  const n = Number(value);
  if (value == null || value === "" || !Number.isFinite(n)) return "";
  return formatter(Intl.NumberFormat, localeOf(lang), { ...LATN, ...options }).format(n);
}

/**
 * The languages someone speaks, named in the site's language and listed the
 * way it lists things: ["pt", "en"] reads "portugais et anglais" in French.
 * An unknown code is shown as given; no code at all is "".
 */
export function formatLanguages(codes, { lang = "fr" } = {}) {
  if (!Array.isArray(codes) || !codes.length) return "";
  const locale = localeOf(lang);
  let names = null;
  try { names = new Intl.DisplayNames([locale], { type: "language" }); } catch { names = null; }
  const list = codes.filter(Boolean).map((code) => { try { return names?.of(String(code)) || String(code); } catch { return String(code); } });
  if (typeof Intl.ListFormat !== "function") return list.join(", ");
  try { return formatter(Intl.ListFormat, locale, { type: "conjunction", style: "long" }).format(list); } catch { return list.join(", "); }
}

/**
 * "6 h 12 min", "4 h", "45 min": hours and minutes, never "372 min".
 * Intl's own unit names, so every language reads its own abbreviations.
 */
export function formatDuration(minutes, { lang = "fr" } = {}) {
  const total = Number(minutes);
  if (minutes == null || minutes === "" || !Number.isFinite(total) || total < 0) return "";
  const rounded = Math.round(total);
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  const locale = localeOf(lang);
  const unit = (value, name) => formatter(Intl.NumberFormat, locale, { ...LATN, style: "unit", unit: name, unitDisplay: "narrow" }).format(value);
  const parts = [];
  if (hours > 0) parts.push(unit(hours, "hour"));
  if (rest > 0 || hours === 0) parts.push(unit(rest, "minute"));
  if (parts.length === 1 || typeof Intl.ListFormat !== "function") return parts.join(" ");
  return formatter(Intl.ListFormat, locale, { type: "unit", style: "narrow" }).format(parts);
}

/**
 * The form of `forms` that `n` takes in the site's language, "#" replaced by
 * the number: plural(3, { one: "# nuit", other: "# nuits" }, { lang: "fr" })
 * is "3 nuits". Arabic has zero, one, two, few, many and other; a form a
 * table leaves out falls back to `other`.
 */
export function plural(n, forms, { lang = "fr" } = {}) {
  if (!forms) return "";
  const category = formatter(Intl.PluralRules, localeOf(lang), {}).select(Number(n));
  const text = forms[category] ?? forms.other ?? "";
  return text.replace(/#/g, formatNumber(n, { lang }));
}

/**
 * A XOF amount in `currency`, exactly as the rest of the site prints it
 * (lib/currencies formatAmount, which SettingsContext.formatPrice wraps).
 */
export function money(xof, { lang = "fr", currency = "XOF", table = DEFAULT_CURRENCIES } = {}) {
  return formatAmount(xof, currency, table, lang);
}

/**
 * The amount the card is debited, as the server states it: already in the
 * charge currency, with its cents ("481,20 USD"). Nothing is converted here.
 */
export function chargeAmount(amount, { lang = "fr", currency = CHARGE_CURRENCY } = {}) {
  const digits = formatNumber(amount, { lang, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return digits ? `${digits}\u00A0${currency}` : "";
}

/**
 * The formats above bound to the visitor's language and currency. Outside a
 * SettingsProvider (a test, an isolated preview) it prints in French and
 * XOF, the provider's own defaults.
 */
export function useFormat() {
  const settings = useSettings();
  const lang = settings?.lang || "fr";
  return {
    lang,
    money: (xof) => (settings?.formatPrice ? settings.formatPrice(xof) : money(xof, { lang })),
    moneyIn: (xof, code) => (settings?.formatIn ? settings.formatIn(xof, code) : money(xof, { lang, currency: code })),
    charge: (amount, currency = settings?.chargeCurrency || CHARGE_CURRENCY) => chargeAmount(amount, { lang, currency }),
    number: (value, options) => formatNumber(value, { ...options, lang }),
    date: (value, options) => formatDate(value, { ...options, lang }),
    time: (value, options) => formatTime(value, { ...options, lang }),
    dateTime: (value, options) => formatDateTime(value, { ...options, lang }),
    dateRange: (start, end, options) => formatDateRange(start, end, { ...options, lang }),
    duration: (minutes) => formatDuration(minutes, { lang }),
    plural: (n, forms) => plural(n, forms, { lang }),
  };
}
